import { useState } from "react";
import { motion } from "framer-motion";
import { FadeUp, RevealLinesOnView } from "@/components/motion/Reveals";
import { useLang } from "@/i18n/LangContext";
import { useContent, resolveUrl } from "@/cms/ContentContext";
import { useLeafTx } from "@/cms/useTexts";
import { useSpacing } from "@/cms/useSpacing";

const BeforeAfter = ({ before, after, testid }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      data-testid={testid}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="relative aspect-[4/5] overflow-hidden frame-clip border border-[#3A3524]/12 cursor-pointer group"
    >
      <img src={before} alt="Before" className="absolute inset-0 w-full h-full object-cover" />
      <motion.div
        animate={{ clipPath: hover ? "inset(0 0 0 0)" : "inset(0 0 0 100%)" }}
        transition={{ duration: 0.9, ease: [0.7, 0, 0.15, 1] }}
        className="absolute inset-0"
      >
        <img src={after} alt="After" className="absolute inset-0 w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#3A3524]/40 via-transparent to-transparent" />
      <div className="absolute top-4 left-4 overline text-[#F5F2EC] bg-[#3A3524]/70 backdrop-blur px-3 py-1.5">
        {hover ? "AFTER" : "BEFORE"}
      </div>
      <div className="absolute bottom-4 right-4 text-[#F5F2EC] font-serif italic text-sm opacity-80">
        Hover
      </div>
    </div>
  );
};

const Portfolio = () => {
  const { t, lang } = useLang();
  const { content } = useContent();
  const tx = useLeafTx();
  const { sectionPy, titleGap, overlineGap, divider } = useSpacing();
  const cmsCases = content?.portfolio || [];
  // Only show cases the user has actually uploaded images for.
  const cases = cmsCases
    .filter((c) => resolveUrl(c.before) || resolveUrl(c.after))
    .map((c) => ({
      before: resolveUrl(c.before) || resolveUrl(c.after),
      after: resolveUrl(c.after) || resolveUrl(c.before),
      label: lang === "pt" ? c.label_pt : c.label_en,
      tag: lang === "pt" ? c.tag_pt : c.tag_en,
    }));

  if (cases.length === 0) {
    return null; // no portfolio to display
  }

  return (
    <section id="portfolio" data-testid="portfolio-section" className={`relative ${sectionPy("portfolio")} ${divider("bg-[#EAE5D9]/40")}`}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <FadeUp>
          <div className={`overline overline-2x text-[#6B6554] ${overlineGap("portfolio")}`}>{tx("portfolio.overline", t.portfolio.overline)}</div>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="text-[#3A3524]/80 text-lg leading-relaxed font-light max-w-3xl text-justify mb-16 md:mb-20">
            {tx("portfolio.sub", t.portfolio.sub)}
          </p>
        </FadeUp>

        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {cases.map((c, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.08, ease: [0.22, 0.61, 0.36, 1] }}
              className={`col-span-12 sm:col-span-6 ${i % 2 === 0 ? "lg:col-span-7" : "lg:col-span-5"} ${i % 2 === 1 ? "lg:mt-16" : ""}`}
            >
              <BeforeAfter
                before={c.before}
                after={c.after}
                testid={`portfolio-case-${i}`}
              />
              <figcaption className="mt-4 flex items-center justify-between text-[#3A3524]">
                <span className="font-serif italic text-lg">{c.label}</span>
                <span className="overline text-[#6B6554]">{c.tag}</span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
